import { AxiosError } from 'axios'

export enum ErrorType {
  VALIDATION = 'validation',
  DUPLICATE = 'duplicate',
  AUTH = 'auth',
  SERVER = 'server',
  ERROR = 'error',
}

export enum DatabaseUploadErrorCode {
  // File validation errors
  INVALID_FILE_TYPE = 'databaseUpload.error.invalid-file-type',
  BAK_FILE_NEEDS_COMPRESSION = 'databaseUpload.error.bak-file-needs-compression',
  INVALID_FILE_NAME = 'databaseUpload.error.invalid-file-name',
  FILE_TOO_LARGE = 'databaseUpload.error.file-too-large',
  
  // Upload errors
  UPLOAD_FAILED = 'databaseUpload.error.upload-failed',
  PROCESSING_FAILED = 'databaseUpload.error.processing-failed',
  
  // Company errors
  COMPANY_ALREADY_EXISTS = 'databaseUpload.error.company-already-exists',
  COMPANY_CREATION_FAILED = 'databaseUpload.error.company-creation-failed',
  
  // Database errors
  DATABASE_RESTORE_FAILED = 'databaseUpload.error.database-restore-failed',
  PAYROLL_UNSUPPORTED = 'databaseUpload.error.payroll-unsupported',
  BACKUP_NOT_COMPANY = 'databaseUpload.error.backup-not-company',
  DATABASE_INVALID = 'databaseUpload.error.database-invalid',
  
  // Auth errors
  USER_NOT_LOGGED_IN = 'databaseUpload.error.user-not-logged-in',
  USER_ID_NOT_FOUND = 'databaseUpload.error.user-id-not-found',
  INSUFFICIENT_PERMISSIONS = 'databaseUpload.error.insufficient-permissions',
  
  // Server errors
  SERVER_ERROR = 'databaseUpload.error.server-error',
  NETWORK_ERROR = 'databaseUpload.error.network-error',
  
  // Generic errors
  UNKNOWN_ERROR = 'databaseUpload.error.unknown',
}

export interface ParsedError {
  errorCode: DatabaseUploadErrorCode
  type: ErrorType
  params?: Record<string, string>
  originalMessage?: string
}

/**
 * Extract error message from axios error response
 */
export function parseErrorMessage(error: unknown): string {
  if (typeof error === 'string') return error

  if (error instanceof Error) {
    const axiosError = error as AxiosError<any>
    
    // Priority 1: error.response.data.errorMessage
    if (axiosError.response?.data?.errorMessage) {
      return axiosError.response.data.errorMessage
    }
    
    // Priority 2: error.response.data.message
    if (axiosError.response?.data?.message) {
      return axiosError.response.data.message
    }
    
    // Priority 3: error.message
    if (error.message) {
      return error.message
    }
  }
  
  // Fallback
  return 'Unknown error'
}

/**
 * Categorize error type based on message content and HTTP status
 */
export function categorizeError(error: unknown, message: string): ErrorType {
  const axiosError = error as AxiosError
  const status = axiosError?.response?.status
  
  // Auth errors
  if (status === 401 || status === 403) {
    return ErrorType.AUTH
  }
  
  // Server errors
  if (status && status >= 500) {
    return ErrorType.SERVER
  }
  
  // Duplicate errors
  const lowerMessage = message.toLowerCase()
  if (lowerMessage.includes('already exists') || lowerMessage.includes('duplicate')) {
    return ErrorType.DUPLICATE
  }
  
  // Validation errors
  if (
    status === 400 ||
    lowerMessage.includes('invalid') ||
    lowerMessage.includes('required') ||
    lowerMessage.includes('missing')
  ) {
    return ErrorType.VALIDATION
  }
  
  // Default error
  return ErrorType.ERROR
}

/**
 * Map error message to error code and extract parameters
 */
export function mapErrorToCode(message: string): { code: DatabaseUploadErrorCode; params?: Record<string, string> } {
  const lowerMessage = message.toLowerCase()
  
  if (lowerMessage.includes('desktop_payroll_unsupported')) {
    return { code: DatabaseUploadErrorCode.PAYROLL_UNSUPPORTED }
  }

  // Company already exists
  if (lowerMessage.includes('already exists')) {
    const match = message.match(/UniqueIdentifier '([^']+)' already exists/)
    if (match) {
      return {
        code: DatabaseUploadErrorCode.COMPANY_ALREADY_EXISTS,
        params: { '0': match[1] },
      }
    }
    return { code: DatabaseUploadErrorCode.COMPANY_ALREADY_EXISTS }
  }
  
  // Permission errors
  if (lowerMessage.includes('permission') || lowerMessage.includes('unauthorized') || lowerMessage.includes('forbidden')) {
    return { code: DatabaseUploadErrorCode.INSUFFICIENT_PERMISSIONS }
  }

  // Failed to create company
  if (lowerMessage.includes('failed to create company') || lowerMessage.includes('company creation')) {
    return { code: DatabaseUploadErrorCode.COMPANY_CREATION_FAILED }
  }

  if (lowerMessage.includes('kopia nie zawiera rozpoznanej bazy firmowej')) {
    return { code: DatabaseUploadErrorCode.BACKUP_NOT_COMPANY }
  }

  // Database restore failed
  if (lowerMessage.includes('restore') || lowerMessage.includes('database')) {
    return { code: DatabaseUploadErrorCode.DATABASE_RESTORE_FAILED }
  }
  
  // Network/connection errors
  if (lowerMessage.includes('network') || lowerMessage.includes('connection')) {
    return { code: DatabaseUploadErrorCode.NETWORK_ERROR }
  }
  
  // Server errors
  if (lowerMessage.includes('server error') || lowerMessage.includes('internal error')) {
    return { code: DatabaseUploadErrorCode.SERVER_ERROR }
  }
  
  // Upload failed
  if (lowerMessage.includes('upload')) {
    return { code: DatabaseUploadErrorCode.UPLOAD_FAILED }
  }
  
  // Processing failed
  if (lowerMessage.includes('processing')) {
    return { code: DatabaseUploadErrorCode.PROCESSING_FAILED }
  }
  
  // Default
  return { code: DatabaseUploadErrorCode.UNKNOWN_ERROR }
}

/**
 * Parse and process error into structured format with error code
 */
export function handleError(error: unknown): ParsedError {
  const originalMessage = parseErrorMessage(error)
  const type = categorizeError(error, originalMessage)
  const { code, params } = mapErrorToCode(originalMessage)
  
  return {
    errorCode: code,
    type,
    params,
    originalMessage,
  }
}

